import { useState, useMemo } from "react";
import { SignatureServices } from "../../../shared/services/api/signature/SignatureServices";
import { useToast } from "../../../shared/hooks";
import { ToastContent } from "../../../shared/components";
import { IDocument } from "../types/types";

// Funções utilitárias
export const getFileTypeFromName = (name: string): ".pdf" | ".docx" | null => {
	const ext = (name || "").toLowerCase().split(".").pop();
	if (ext === "pdf") return ".pdf";
	if (ext === "docx") return ".docx";
	return null;
};

interface UseDocumentsProps {
	envelopeId?: string;
	documents: IDocument[];
	onChange: (nextDocuments: IDocument[]) => void;
	envelopeStatus?: string;
}

export function useEnvelopeDocuments({ envelopeId, documents, onChange, envelopeStatus }: UseDocumentsProps) {
	const toast = useToast();
	const [isSubmitting, setIsSubmitting] = useState(false);

	const existingNames = useMemo(() => new Set(documents.map((d) => (d.filename || "").trim().toLowerCase())), [documents]);

	const validateFilename = (filename: string) => {
		const nameKey = (filename || "").trim().toLowerCase();
		if (existingNames.has(nameKey)) {
			throw new Error("Já existe um documento com este nome.");
		}
	};

	// Lógica para upload de documento
	const handleAdd = async (file: File, filename: string) => {
		if (!envelopeId) {
			toast.present({
				type: "error",
				position: "top-right",
				messageOrContent: <ToastContent title="Erro" content="Crie e salve o envelope para adicionar documentos." />,
			});
			return;
		}

		if (envelopeStatus !== "draft") {
			toast.present({
				type: "error",
				position: "top-right",
				messageOrContent: <ToastContent title="Erro" content="Documentos só podem ser adicionados em envelopes com status de Rascunho." />,
			});
			return;
		}

		try {
			validateFilename(filename);

			setIsSubmitting(true);
			const res = await SignatureServices.createDocument(envelopeId, file, filename);
			if (res instanceof Error) {
				throw new Error("Erro ao incluir documento.");
			}

			const next = [...documents, res];
			onChange(next);

			toast.present({
				type: "success",
				position: "top-right",
				messageOrContent: <ToastContent title="Sucesso" content="Documento incluído." />,
			});
		} catch (e: any) {
			toast.present({
				type: "error",
				position: "top-right",
				messageOrContent: <ToastContent title="Erro" content={"Erro ao incluir documento."} />,
			});
		} finally {
			setIsSubmitting(false);
		}
	};

	// Lógica para download de documento
	const handleDownload = async (documentId: string) => {
		if (!envelopeId || !documentId) return;

		try {
			const fileUrl = await SignatureServices.downloadDocument(envelopeId, documentId);
			if (fileUrl instanceof Error) {
				throw new Error(fileUrl.message);
			}

			window.open(fileUrl, "_blank");
		} catch (e: any) {
			toast.present({
				type: "error",
				position: "top-right",
				messageOrContent: <ToastContent title="Erro" content={"Erro ao baixar o documento."} />,
			});
		}
	};

	// ✅ CORREÇÃO: Lógica para remover um documento, agora verificando o retorno do serviço
	const handleRemove = async (documentId: string) => {
		if (!envelopeId || !documentId) return;

		try {
			const res = await SignatureServices.deleteDocument(envelopeId, documentId);
			if (res instanceof Error) {
				throw new Error("Erro ao remover documento.");
			}

			const nextDocuments = documents.filter((d) => d.id !== documentId);
			onChange(nextDocuments);
			toast.present({
				type: "success",
				position: "top-right",
				messageOrContent: <ToastContent title="Removido" content="Documento removido com sucesso." />,
			});
		} catch (e: any) {
			toast.present({
				type: "error",
				position: "top-right",
				messageOrContent: <ToastContent title="Erro" content={"Erro ao remover documento."} />,
			});
		}
	};

	return {
		isSubmitting,
		canAddDocument: envelopeStatus === "draft",
		handleAdd,
		handleDownload,
		handleRemove,
	};
}
